import { useCallback, useEffect, useState } from 'react';
import { canShowOnboarding, hideOnboarding } from '../../../services/onboarding/OnboardingService';

interface UseAnnouncementOptions {
  /** Entry name in the per-user `onboarding` table (same store as FirstProjectOnboarding). */
  entry: string;
  /** Nothing is checked or persisted while false (e.g. the account lacks the addon). */
  enabled: boolean;
  /** On the announced tab itself nothing opens; a still-unseen entry is marked seen right away. */
  isOnTab: boolean;
  /** Extra condition for OPENING only (not for the on-tab marking), e.g. the Ads paid/trial-age rule. */
  canOpen?: boolean;
}

// Visibility + "seen" persistence of a feature announcement. The popup comes back on every load until the
// user reacts once (`dismiss` is wired to close, "Maybe later" and the CTA). Whoever reaches the tab knows
// the feature: the entry is marked seen regardless of `canOpen`, so the promo never follows them to other
// pages later (e.g. a young trial that found the tab on its own is not promoted it two weeks later).
export const useAnnouncement = ({ entry, enabled, isOnTab, canOpen = true }: UseAnnouncementOptions) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // The hook stays mounted (in `Layout`) while the popup renders nothing on the tab. A popup left open
    // elsewhere must not survive the visit: without this it came back on the next page although the entry
    // was already marked seen (CR 2026-10-05). Synchronous on purpose — independent of the lookup result.
    if (isOnTab) {
      setVisible(false);
    }
    let cancelled = false;
    // `canShowOnboarding` resolves with the error object on failure — only a literal `true` counts.
    canShowOnboarding(entry)
      .then((canShow) => {
        if (cancelled || canShow !== true) {
          return;
        }
        if (isOnTab) {
          void hideOnboarding(entry);
        } else if (canOpen) {
          setVisible(true);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [entry, enabled, isOnTab, canOpen]);

  const dismiss = useCallback(() => {
    setVisible(false);
    void hideOnboarding(entry);
  }, [entry]);

  return { visible, dismiss };
};
